import React, { useEffect, useMemo, useState } from "react";
import AppContainer from "@src/common/app_container";
import { useAuth0 } from "@auth0/auth0-react";
import { LANGUAGE_CATALOG } from "@src/lib/lang_catalog";

type SharedVoice = {
    voice_id: string;
    name?: string;
    language?: string;
    locale?: string;
    accent?: string;
    age?: string;
    gender?: string;
    description?: string;
    preview_url?: string;
    verified_languages?: { language?: string; locale?: string; accent?: string }[];
};

type TaskQueueResponse = { id: string };

type GenerateMediaResult = {
    outputs: { permalink: string }[];
};

const OUTPUT_FORMATS = ["mp3_44100_128", "mp3_44100_64", "wav_44100"];

// Expanded model list for variety (availability can vary by account/region)
const MODELS = [
    "official_flash_v2_5",
    "official_flash_v2",
    "official_v2_flash",
    "official_turbo_v2_5",
    "official_turbo_v2",
    "official_multilingual_v2",
    "official_v2",
];

type LanguageOption = { code: string; label: string; flag: string };

function defaultLanguage(): LanguageOption {
    const en = LANGUAGE_CATALOG.find((x) => x.code === "en");
    return en || { code: "en", label: "English", flag: "🇺🇸" };
}

function normalizeAge(a?: string | null) {
    if (!a) return null;
    const s = a.toLowerCase().trim().replaceAll("-", "_");
    if (s === "middle_age" || s === "middle_aged") return "middle_age";
    if (s === "young" || s === "youth") return "young";
    if (s === "old" || s === "elderly" || s === "senior") return "old";
    return null;
}

function pillClass(kind: "gender" | "language" | "accent" | "age") {
    switch (kind) {
        case "gender":
            return "bg-blue-100 text-blue-700";
        case "language":
            return "bg-indigo-100 text-indigo-700";
        case "accent":
            return "bg-purple-100 text-purple-700";
        case "age":
            return "bg-amber-100 text-amber-800";
    }
}

const PAGE_SIZE = 50;

export default function VoicesIndex() {
    const { getAccessTokenSilently } = useAuth0();

    const [allVoices, setAllVoices] = useState<SharedVoice[]>([]);
    const [results, setResults] = useState<SharedVoice[]>([]);

    const [language, setLanguage] = useState<LanguageOption | null>(defaultLanguage());
    const [accent, setAccent] = useState<string>("any");
    const [age, setAge] = useState<string>("any");
    const [gender, setGender] = useState<string>("any");
    const [loading, setLoading] = useState(false);

    const [selectedVoice, setSelectedVoice] = useState<SharedVoice | null>(null);
    const [ttsText, setTtsText] = useState<string>("This is a test from Official AI.");
    const [modelId, setModelId] = useState<string>("official_flash_v2_5");
    const [outputFormat, setOutputFormat] = useState<string>("mp3_44100_128");
    const [stability, setStability] = useState<number>(0.4);
    const [similarity, setSimilarity] = useState<number>(0.7);
    const [style, setStyle] = useState<number>(0.0);
    const [speakerBoost, setSpeakerBoost] = useState<boolean>(true);
    const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
    const [generating, setGenerating] = useState<boolean>(false);

    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    const [showAccountVoices, setShowAccountVoices] = useState(false);
    const [accountLoading, setAccountLoading] = useState(false);
    const [accountVoices, setAccountVoices] = useState<SharedVoice[]>([]);
    const [accountSearch, setAccountSearch] = useState("");
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const toBackendModel = (id: string) => id.startsWith("official_") ? id.replace(/^official_/, "eleven_") : id;

    // pagination state
    const [page, setPage] = useState<number>(1);
    const pageCount = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
    const startIdx = (page - 1) * PAGE_SIZE;
    const endIdx = Math.min(results.length, startIdx + PAGE_SIZE);
    const visibleResults = results.slice(startIdx, endIdx);

    const authHeaders = async () => {
        const token = await getAccessTokenSilently({
            authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUD },
        });
        return {
            authorization: `Bearer ${token}`,
            "content-type": "application/json",
        };
    };

    const languageOptions: LanguageOption[] = useMemo(() => {
        const out = LANGUAGE_CATALOG.map((x) => ({ code: x.code, label: x.label, flag: x.flag }));
        out.sort((a, b) => a.label.localeCompare(b.label));
        return out;
    }, []);

    const accentOptions = useMemo(() => {
        if (!language) return ["any"];
        const set: Record<string, true> = {};
        for (let i = 0; i < allVoices.length; i++) {
            const v = allVoices[i];
            if (v.language === language.code && v.accent) set[v.accent] = true;
            const vls = v.verified_languages || [];
            for (let j = 0; j < vls.length; j++) {
                const vl = vls[j];
                if (vl.language === language.code && vl.accent) set[vl.accent] = true;
            }
        }
        const arr = Object.keys(set).sort();
        return ["any", ...arr];
    }, [allVoices, language]);

    const ageOptions = ["any", "young", "middle_age", "old"];

    const loadAllPages = async (lang?: string | null, g?: string | null, refresh?: boolean) => {
        const headers = await authHeaders();
        const pageSize = 100;
        let pageNum = 1;
        const acc: SharedVoice[] = [];
        while (true) {
            const params = new URLSearchParams();
            if (lang) params.set("language", lang);
            if (g && g !== "any") params.set("gender", g);
            params.set("page_size", String(pageSize));
            params.set("page", String(pageNum));
            if (refresh) params.set("refresh", "true");
            const r = await fetch(
                `${process.env.REACT_APP_API_URL}/generative/voices/shared?${params.toString()}`,
                { headers }
            );
            const data: SharedVoice[] = await r.json();
            const batch = Array.isArray(data) ? data : [];
            acc.push(...batch);
            if (batch.length < pageSize) break;
            if (pageNum >= 25) break;
            pageNum++;
        }
        return acc;
    };

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const acc = await loadAllPages(language?.code || null, gender);
                setAllVoices(acc);
                const cleaned = acc.filter(
                    (v) => normalizeAge(v.age) !== null
                );

                setResults(cleaned);
                setPage(1);
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setAccent("any");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language]);

    // reset to page 1 whenever results change
    useEffect(() => {
        setPage(1);
    }, [results]);

    const applyFilters = async (refresh = false) => {
        setLoading(true);
        try {
            const acc = await loadAllPages(language?.code || null, gender, refresh);
            let list = acc;
            list = list.filter((v) => normalizeAge(v.age) !== null);
            if (age !== "any") list = list.filter((v) => normalizeAge(v.age) === age);
            if (accent !== "any") {
                list = list.filter((v) => {
                    if (v.accent === accent && v.language === language?.code) return true;
                    const vls = v.verified_languages || [];
                    for (let i = 0; i < vls.length; i++) {
                        const vl = vls[i];
                        if (vl.language === (language?.code || "") && vl.accent === accent) return true;
                    }
                    return false;
                });
            }
            setAllVoices(acc);
            setResults(list);
            setSelectedVoice(null);
            setGeneratedUrl(null);
            setExpanded(new Set());
            setPage(1);
        } finally {
            setLoading(false);
        }
    };

    const resetFilters = async () => {
        setLanguage(defaultLanguage());
        setAccent("any");
        setAge("any");
        setGender("any");
        await applyFilters();
    };

    const synthesize = async () => {
        if (!selectedVoice || !ttsText.trim()) return;
        setGenerating(true);
        setGeneratedUrl(null);
        const wait = (ms: number) => new Promise(res => setTimeout(res, ms));

        try {
            const headers = await authHeaders();
            const body = {
                voiceId: selectedVoice.voice_id,
                parentTaskId: null,
                s3KeyPrefix: null,
                tts: {
                    text: ttsText,
                    model_id: toBackendModel(modelId),
                    output_format: outputFormat,
                    voice_settings: {
                        stability,
                        similarity_boost: similarity,
                        style,
                        use_speaker_boost: speakerBoost,
                    },
                },
            };

            const r = await fetch(`${process.env.REACT_APP_API_URL}/generative/tts/synthesize`, {
                method: "POST",
                headers,
                body: JSON.stringify(body),
            });
            const task: TaskQueueResponse & { payload?: string } = await r.json();

            let immediateUrl: string | null = null;
            if (task?.payload) {
                try {
                    const parsed = JSON.parse(task.payload);
                    if (parsed?.permalink && typeof parsed.permalink === "string") {
                        immediateUrl = parsed.permalink;
                    }
                } catch {}
            }
            if (immediateUrl) {
                setGeneratedUrl(immediateUrl);
                return;
            }

            const maxTries = 20;
            const intervalMs = 1000;
            let url: string | null = null;

            for (let i = 0; i < maxTries; i++) {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/generative/task/${task.id}`, { headers });
                const result: GenerateMediaResult = await res.json();
                const link = result?.outputs?.[0]?.permalink || null;
                if (link) { url = link; break; }
                await wait(intervalMs);
            }

            setGeneratedUrl(url);
        } finally {
            setGenerating(false);
        }
    };

    const toggleExpanded = (id: string) => {
        const next = new Set(expanded);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setExpanded(next);
    };

    const copyVoiceId = async () => {
        if (!selectedVoice?.voice_id) return;
        try {
            await navigator.clipboard.writeText(selectedVoice.voice_id);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        } catch {
            const ta = document.createElement("textarea");
            ta.value = selectedVoice.voice_id;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand("copy");
            document.body.removeChild(ta);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        }
    };
    const [copied, setCopied] = useState(false);

    const openAccountVoices = async () => {
        setShowAccountVoices(true);
        setAccountLoading(true);
        try {
            const headers = await authHeaders();
            const r = await fetch(`${process.env.REACT_APP_API_URL}/generative/voices/account`, { headers });
            const data = await r.json();
            const arr: SharedVoice[] = Array.isArray(data) ? data : (Array.isArray(data?.voices) ? data.voices : []);
            setAccountVoices(arr);
        } finally {
            setAccountLoading(false);
        }
    };

    const filteredAccountVoices = useMemo(() => {
        const q = accountSearch.trim().toLowerCase();
        if (!q) return accountVoices;
        return accountVoices.filter(v => {
            const name = (v.name || "").toLowerCase();
            const id = (v.voice_id || "").toLowerCase();
            return name.includes(q) || id.includes(q);
        });
    }, [accountSearch, accountVoices]);

    const deleteAccountVoice = async (voiceId: string) => {
        if (!voiceId) return;
        if (!window.confirm("Delete this voice? This cannot be undone.")) return;
        setDeletingId(voiceId);
        try {
            const headers = await authHeaders();
            const res = await fetch(`${process.env.REACT_APP_API_URL}/generative/voices/${voiceId}`, {
                method: "DELETE",
                headers
            });
            if (res.ok) {
                setAccountVoices(prev => prev.filter(v => v.voice_id !== voiceId));
                if (selectedVoice?.voice_id === voiceId) setSelectedVoice(null);
            } else {
                const txt = await res.text();
                alert(txt || "Failed to delete voice");
            }
        } catch {
            alert("Failed to delete voice");
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <AppContainer>
            <div className="space-y-6">
                <h1 className="text-2xl font-bold">Voice Blueprint</h1>

                {/* Filters */}
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Language</label>
                        <select
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            value={language?.code || ""}
                            onChange={(e) => {
                                const sel = LANGUAGE_CATALOG.find((x) => x.code === e.target.value);
                                setLanguage(sel ? { code: sel.code, label: sel.label, flag: sel.flag } : null);
                            }}
                        >
                            {languageOptions.map((opt) => (
                                <option key={opt.code} value={opt.code}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Accent</label>
                        <select
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            value={accent}
                            onChange={(e) => setAccent(e.target.value)}
                        >
                            {accentOptions.map((a) => (
                                <option key={a} value={a}>
                                    {a === "any" ? "Any" : a}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Gender</label>
                        <select
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            value={gender}
                            onChange={(e) => setGender(e.target.value)}
                        >
                            {["any", "male", "female", "neutral"].map((g) => (
                                <option key={g} value={g}>
                                    {g === "any" ? "Any" : g[0].toUpperCase() + g.slice(1)}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Age</label>
                        <select
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            value={age}
                            onChange={(e) => setAge(e.target.value)}
                        >
                            {ageOptions.map((a) => (
                                <option key={a} value={a}>
                                    {a === "any" ? "Any" : a.replaceAll("_", " ").replaceAll("-", " ")}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="flex items-center justify-between">
                    <div className="flex gap-2">
                        <button onClick={() => applyFilters(false)} className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500">
                            Apply filters
                        </button>
                        <button onClick={resetFilters} className="inline-flex items-center rounded-md bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-200">
                            Reset
                        </button>
                        <button
                            onClick={() => applyFilters(true)}
                            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50"
                        >
                            Refresh voices
                        </button>
                    </div>
                    <button onClick={openAccountVoices} className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50">
                        My voices
                    </button>
                </div>

                {/* Results + Synthesize panel */}
                <div className="grid gap-4 lg:grid-cols-3">
                    {/* Results */}
                    <div className="lg:col-span-2">
                        {loading ? (
                            <div className="py-6 text-gray-600">Loading…</div>
                        ) : results.length === 0 ? (
                            <div className="py-6 text-gray-600">No voices found</div>
                        ) : (
                            <>
                                <div className="mb-2 text-sm text-gray-600">
                                    Showing {results.length === 0 ? 0 : startIdx + 1}–{endIdx} of {results.length}
                                </div>

                                <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                    {visibleResults.map((v) => {
                                        const isSelected = selectedVoice?.voice_id === v.voice_id;
                                        const normalizedAge = normalizeAge(v.age);
                                        const desc = v.description || "";
                                        const isExpanded = expanded.has(v.voice_id);
                                        const cutoff = 140;
                                        const needsClamp = desc.length > cutoff;
                                        const shown = isExpanded || !needsClamp ? desc : desc.slice(0, cutoff) + "…";

                                        return (
                                            <li
                                                key={v.voice_id}
                                                className={`flex h-full flex-col rounded-md border p-4 ${
                                                    isSelected ? "ring-2 ring-indigo-500" : "border-gray-200"
                                                }`}
                                            >
                                                <div className="flex items-center">
                                                    <div className="font-semibold truncate" title={v.name || v.voice_id}>
                                                        {v.name || v.voice_id}
                                                    </div>
                                                </div>
                                                <div className="mt-2 flex flex-wrap gap-1.5">
                          <span className={`rounded-full px-2 py-0.5 text-xs ${pillClass("language")}`}>
                            {(v.language || "—").toUpperCase()}
                          </span>
                                                    <span className={`rounded-full px-2 py-0.5 text-xs ${pillClass("accent")}`}>
                            {v.accent ? v.accent : "any"}
                          </span>
                                                    <span className={`rounded-full px-2 py-0.5 text-xs ${pillClass("gender")}`}>
                            {v.gender || "any"}
                          </span>
                                                    {normalizedAge && (
                                                        <span className={`rounded-full px-2 py-0.5 text-xs ${pillClass("age")}`}>
                              {normalizedAge.replaceAll("_", " ").replaceAll("-", " ")}
                            </span>
                                                    )}
                                                </div>

                                                {desc && (
                                                    <div className="mt-2 text-sm text-gray-600">
                                                        {shown}
                                                        {needsClamp && (
                                                            <button
                                                                className="ml-1 text-indigo-600 underline"
                                                                onClick={() => toggleExpanded(v.voice_id)}
                                                            >
                                                                {isExpanded ? "Show less" : "Show more"}
                                                            </button>
                                                        )}
                                                    </div>
                                                )}

                                                {v.preview_url && (
                                                    <audio className="mt-3 w-full" controls src={v.preview_url}>
                                                        Your browser does not support the audio element.
                                                    </audio>
                                                )}

                                                <div className="mt-auto pt-3">
                                                    <button
                                                        className="w-full rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
                                                        onClick={() => {
                                                            setSelectedVoice(v);
                                                            setGeneratedUrl(null);
                                                        }}
                                                    >
                                                        {isSelected ? "Selected" : "Choose voice"}
                                                    </button>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>

                                {/* Pagination controls */}
                                <div className="mt-4 flex items-center justify-between">
                                    <button
                                        className="rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-900 hover:bg-gray-200 disabled:opacity-50"
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        disabled={page <= 1}
                                    >
                                        Previous
                                    </button>
                                    <div className="text-sm text-gray-700">
                                        Page {page} of {pageCount}
                                    </div>
                                    <button
                                        className="rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-900 hover:bg-gray-200 disabled:opacity-50"
                                        onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                                        disabled={page >= pageCount}
                                    >
                                        Next
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Synthesize */}
                    <div className="lg:col-span-1">
                        <div className="rounded-md border border-gray-200 p-4">
                            <div className="flex items-center justify-between">
                                <div className="text-sm font-semibold">Synthesize</div>
                                <button
                                    className={`rounded-md px-2 py-1 text-xs font-semibold ${
                                        selectedVoice
                                            ? "bg-gray-100 text-gray-900 hover:bg-gray-200"
                                            : "bg-gray-100 text-gray-400"
                                    }`}
                                    disabled={!selectedVoice}
                                    onClick={copyVoiceId}
                                    title={selectedVoice?.voice_id || ""}
                                >
                                    {copied ? "Copied!" : "Copy Voice ID"}
                                </button>
                            </div>

                            <div className="mt-2">
                <textarea
                    className="w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    rows={4}
                    value={ttsText}
                    onChange={(e) => setTtsText(e.target.value)}
                    placeholder="Type text to speak…"
                />
                            </div>

                            <div className="mt-3 grid grid-cols-2 gap-3">
                                <div>
                                    <label className="mb-1 block text-xs text-gray-600">Model</label>
                                    <select
                                        className="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                        value={modelId}
                                        onChange={(e) => setModelId(e.target.value)}
                                    >
                                        {MODELS.map((m) => (
                                            <option key={m} value={m}>
                                                {m}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="mb-1 block text-xs text-gray-600">Format</label>
                                    <select
                                        className="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                        value={outputFormat}
                                        onChange={(e) => setOutputFormat(e.target.value)}
                                    >
                                        {OUTPUT_FORMATS.map((f) => (
                                            <option key={f} value={f}>
                                                {f}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="mt-3 grid grid-cols-2 gap-3">
                                <div>
                                    <label className="mb-1 block text-xs text-gray-600">
                                        Stability {stability.toFixed(2)}
                                    </label>
                                    <input
                                        type="range"
                                        min={0}
                                        max={1}
                                        step={0.01}
                                        value={stability}
                                        onChange={(e) => setStability(parseFloat(e.target.value))}
                                        className="w-full"
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-xs text-gray-600">
                                        Similarity {similarity.toFixed(2)}
                                    </label>
                                    <input
                                        type="range"
                                        min={0}
                                        max={1}
                                        step={0.01}
                                        value={similarity}
                                        onChange={(e) => setSimilarity(parseFloat(e.target.value))}
                                        className="w-full"
                                    />
                                </div>
                            </div>

                            <div className="mt-3">
                                <label className="mb-1 block text-xs text-gray-600">Style {style.toFixed(2)}</label>
                                <input
                                    type="range"
                                    min={0}
                                    max={1}
                                    step={0.01}
                                    value={style}
                                    onChange={(e) => setStyle(parseFloat(e.target.value))}
                                    className="w-full"
                                />
                            </div>

                            <div className="mt-3 flex items-center gap-2">
                                <input
                                    id="enhance"
                                    type="checkbox"
                                    checked={speakerBoost}
                                    onChange={(e) => setSpeakerBoost(e.target.checked)}
                                />
                                <label htmlFor="enhance" className="text-sm text-gray-700">
                                    Enhance voice (speaker boost)
                                </label>
                            </div>

                            <button
                                onClick={synthesize}
                                disabled={!selectedVoice || !ttsText.trim() || generating}
                                className={`mt-4 inline-flex w-full items-center justify-center rounded-md px-4 py-2 text-sm font-semibold ${
                                    !selectedVoice || !ttsText.trim() || generating
                                        ? "bg-gray-300 text-gray-600"
                                        : "bg-indigo-600 text-white hover:bg-indigo-500"
                                }`}
                            >
                                {generating ? "Generating…" : "Synthesize"}
                            </button>

                            {generatedUrl && (
                                <div className="mt-4">
                                    <audio className="w-full" controls src={generatedUrl} />
                                    <a
                                        className="mt-2 inline-flex items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100"
                                        href={generatedUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                            <path d="M3 14a1 1 0 011-1h3v2H5v2h10v-2h-2v-2h3a1 1 0 011 1v3a2 2 0 01-2 2H5a2 2 0 01-2-2v-3z" />
                                            <path d="M9 3a1 1 0 112 0v7.586l2.293-2.293a1 1 0 111.414 1.414l-4 4-.094.083a1 1 0 01-1.32-.083l-4-4A1 1 0 115.707 8.293L8 10.586V3z" />
                                        </svg>
                                        Download file
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {showAccountVoices && (
                    <div className="fixed inset-0 z-50">
                        <div className="absolute inset-0 bg-black/50" onClick={() => setShowAccountVoices(false)} />
                        <div className="absolute right-0 top-0 h-full w-full max-w-xl bg-white shadow-2xl flex flex-col">
                            <div className="flex items-center justify-between border-b px-6 py-4">
                                <h2 className="text-lg font-semibold">My voices</h2>
                                <button
                                    className="rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-900 hover:bg-gray-200"
                                    onClick={() => setShowAccountVoices(false)}
                                >
                                    Close
                                </button>
                            </div>
                            <div className="border-b px-6 py-3">
                                <input
                                    value={accountSearch}
                                    onChange={(e) => setAccountSearch(e.target.value)}
                                    placeholder="Search by name or voice ID…"
                                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                                />
                            </div>
                            <div className="flex-1 overflow-y-auto">
                                {accountLoading ? (
                                    <div className="p-6 text-gray-600">Loading…</div>
                                ) : filteredAccountVoices.length === 0 ? (
                                    <div className="p-6 text-gray-600">No voices found</div>
                                ) : (
                                    <ul className="divide-y">
                                        {filteredAccountVoices.map((v) => {
                                            const isSel = selectedVoice?.voice_id === v.voice_id;
                                            return (
                                                <li key={v.voice_id} className={`p-4 ${isSel ? "bg-indigo-50" : ""}`}>
                                                    <div className="flex items-center justify-between">
                                                        <div className="min-w-0">
                                                            <div className="truncate font-semibold" title={v.name || v.voice_id}>
                                                                {v.name || v.voice_id}
                                                            </div>
                                                            {v.gender && (
                                                                <div className="text-xs text-gray-500">{v.gender}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {v.preview_url && (
                                                        <audio className="mt-3 w-full" controls src={v.preview_url} />
                                                    )}
                                                    <div className="mt-3 grid grid-cols-2 gap-2">
                                                        <button
                                                            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
                                                            onClick={() => {
                                                                setSelectedVoice(v);
                                                                setGeneratedUrl(null);
                                                                setShowAccountVoices(false);
                                                            }}
                                                        >
                                                            Use voice
                                                        </button>
                                                        <button
                                                            className={`rounded-md px-3 py-2 text-sm font-semibold ${
                                                                deletingId === v.voice_id ? "bg-red-300 text-white" : "bg-red-600 text-white hover:bg-red-500"
                                                            }`}
                                                            onClick={() => deleteAccountVoice(v.voice_id)}
                                                            disabled={deletingId === v.voice_id}
                                                        >
                                                            {deletingId === v.voice_id ? "Deleting…" : "Delete"}
                                                        </button>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AppContainer>
    );
}
